
const libPictView = require('pict-view');

// The pict-section-picker view hash for the "Link a Form Definition" control. Doubles as the id of
// the DOM element it mounts into, and as the key createPicker reuses across repaints.
const RNI_PICKER_HASH = 'PF-RNI-Picker';
// AppData address the picker reads/writes its selection through.
const RNI_PICKER_VALUE_ADDRESS = 'AppData.ProjectForms.LinkIDReportNamedInstance';

class ReportNamedInstanceProjectForms extends libPictView
{
	constructor(pFable, pOptions, pServiceHash)
	{
		super(pFable, pOptions, pServiceHash);

		/** @type {any} */
		this.pict;

		// Project list state
		this._Projects = [];
		this._IsLoadingProjects = false;
		this._ProjectSearchTerm = '';
		this._ProjectSearchDebounceTimer = null;
		this._ProjectsLoadToken = 0;

		// Selected project state
		this._SelectedProject = null;

		// Join records for the selected project
		this._ProjectJoins = [];
		this._IsLoadingJoins = false;
		this._JoinsLoadToken = 0;
		this._IsRepairingDaily = false;

		// Available RNIs for linking
		this._AvailableRNIs = [];
		this._IsLoadingRNIs = false;

		// LIMS Concierge gate config for the selected project (L5 only).
		// Populated lazily after project selection; null while loading or unread.
		this._ConciergeConfig = null;
		this._IsLoadingConciergeConfig = false;

		// Bulk-add modal state (driven by the injected FormGroups option; null when not provided => hidden).
		const tmpFormGroups = (this.options && this.options.FormGroups) || null;
		this._BulkAddSelectedGroup = (tmpFormGroups && Object.keys(tmpFormGroups)[0]) || null;
		this._BulkAddCandidates = [];
	}

	// ---- Top-level navigation entry ----

	onNavigate()
	{
		this._SelectedProject = null;
		this._ProjectJoins = [];
		this._Projects = [];

		// Home for the picker's ValueAddress; reset so a selection from a previous visit cannot be
		// read back as a pending link on this one. Null, not 0 — the picker renders any non-null
		// bound value as its label, so a numeric 0 would paint a literal "0" over the placeholder.
		if (!this.AppData.ProjectForms) { this.AppData.ProjectForms = {}; }
		this.AppData.ProjectForms.LinkIDReportNamedInstance = null;

		this.render('PF-Main-Container', this.options.DefaultDestinationAddress);
		this._loadProjects();
	}

	// ---- Project list ----

	_buildProjectSearchFilter(pSearchTerm)
	{
		const tmpTerm = (pSearchTerm || '').trim();
		if (!tmpTerm)
		{
			return 'FBV~IDProject~GT~0~FSF~Name~ASC~0';
		}

		const tmpEncodedLike = encodeURIComponent(`%${tmpTerm}%`);
		const tmpIsNumeric = /^\d+$/.test(tmpTerm);

		if (tmpIsNumeric)
		{
			return `FOP~0~(~0~FBV~Name~LK~${tmpEncodedLike}~FBVOR~IDProject~EQ~${tmpTerm}~FCP~0~)~0~FSF~Name~ASC~0`;
		}

		return `FBV~Name~LK~${tmpEncodedLike}~FSF~Name~ASC~0`;
	}

	_loadProjects()
	{
		this._IsLoadingProjects = true;
		this._renderProjectList();

		const tmpToken = ++this._ProjectsLoadToken;
		const tmpFilter = this._buildProjectSearchFilter(this._ProjectSearchTerm);

		this.pict.HeadlightRestClient.getJSON(
			`Projects/FilteredTo/${tmpFilter}/0/100`,
			(pError, pBody) =>
			{
				if (tmpToken !== this._ProjectsLoadToken) { return; }

				this._IsLoadingProjects = false;

				if (pError)
				{
					this.pict.log.error(`Error loading projects: ${pError}`);
					this._Projects = [];
				}
				else
				{
					this._Projects = Array.isArray(pBody) ? pBody : [];
				}

				this._renderProjectList();
			}
		);
	}

	selectProject(pIDProject)
	{
		const tmpProject = this._Projects.find((pP) => pP.IDProject === pIDProject);
		if (!tmpProject) { return; }

		this._SelectedProject = tmpProject;
		this._ProjectJoins = [];
		this._AvailableRNIs = [];
		this._ConciergeConfig = null;

		this._renderSelectedProjectHeader();
		this._loadJoinsForProject(pIDProject);
		this._loadAvailableRNIs();
		if (this._isL5User())
		{
			this._loadLIMSConciergeConfig(pIDProject);
		}
		this._refreshL5Sections();
	}

	deselectProject()
	{
		this._SelectedProject = null;
		this._ProjectJoins = [];
		this._AvailableRNIs = [];
		this._ConciergeConfig = null;

		this._clearRNIPicker();
		this._renderRNIPicker();
		this._renderSelectedProjectHeader();
		this._renderJoinsTable();
		this._refreshL5Sections();
	}

	/**
	 * IDCustomer of the currently selected project, or 0 if none / malformed.
	 *
	 * Why: super-admin users have read access across customers, so we cannot
	 * derive customer scope from `AppData.CurrentUser.CustomerID` for any
	 * project-scoped operation — that would either pull no records (if filter
	 * is honored) or all customers' records (depending on row-level auth).
	 * The project record is the authoritative source of customer scope.
	 */
	_getSelectedProjectIDCustomer()
	{
		if (!this._SelectedProject) { return 0; }
		const tmpID = parseInt(this._SelectedProject.IDCustomer, 10);
		return Number.isFinite(tmpID) && tmpID > 0 ? tmpID : 0;
	}

	// ---- Joins for selected project ----

	_loadJoinsForProject(pIDProject)
	{
		this._IsLoadingJoins = true;
		this._renderJoinsTable();

		const tmpToken = ++this._JoinsLoadToken;

		this.pict.HeadlightRestClient.getJSON(
			`ReportNamedInstanceProjectJoins/FilteredTo/FBV~IDProject~EQ~${pIDProject}/0/500`,
			(pError, pBody) =>
			{
				if (tmpToken !== this._JoinsLoadToken)
				{
					return;
				}

				this._IsLoadingJoins = false;

				if (pError)
				{
					this.pict.log.error(`Error loading project joins: ${pError}`);
					this._ProjectJoins = [];
				}
				else
				{
					this._ProjectJoins = Array.isArray(pBody) ? pBody : [];
				}

				// Resolve RNI names for each join
				this._resolveRNIMetadata(() =>
				{
					if (tmpToken !== this._JoinsLoadToken)
					{
						return;
					}

					this._renderJoinsTable();
					// LIMS warning depends on the joins-with-resolved-names list.
					this._renderLIMSConciergeStatus();
				});
			}
		);
	}

	/**
	 * Decorate each join in `_ProjectJoins` with `_RNIName`, `_RNIHash` and `_RNIFormOnly`.
	 *
	 * Reads the whole set through the list endpoint rather than per-ID: the singular
	 * ReportNamedInstance read runs `Read-PostOperation`, not `Reads-PostOperation`, so it never
	 * merges the archetype defaults into `Options` — `Options.FormOnly` is only trustworthy here.
	 *
	 * @param {() => void} fCallback
	 */
	_resolveRNIMetadata(fCallback)
	{
		if (this._ProjectJoins.length === 0)
		{
			return fCallback();
		}

		const tmpIDList = [];
		for (let i = 0; i < this._ProjectJoins.length; i++)
		{
			const tmpID = this._ProjectJoins[i].IDReportNamedInstance;
			if (tmpID > 0 && tmpIDList.indexOf(tmpID) < 0)
			{
				tmpIDList.push(tmpID);
			}
		}

		const fDecorate = (pRNIByID) =>
		{
			for (let i = 0; i < this._ProjectJoins.length; i++)
			{
				const tmpJoin = this._ProjectJoins[i];
				const tmpRNI = pRNIByID[tmpJoin.IDReportNamedInstance];

				if (tmpRNI)
				{
					tmpJoin._RNIName = tmpRNI.Name || `RNI ${tmpRNI.IDReportNamedInstance}`;
					tmpJoin._RNIHash = tmpRNI.Hash || '';
					tmpJoin._RNIFormOnly = !!(tmpRNI.Options && tmpRNI.Options.FormOnly);
				}
				else if (tmpJoin.IDReportNamedInstance > 0)
				{
					tmpJoin._RNIName = `RNI ${tmpJoin.IDReportNamedInstance}`;
					tmpJoin._RNIHash = '';
					tmpJoin._RNIFormOnly = false;
				}
				else
				{
					tmpJoin._RNIName = '(Unassigned)';
					tmpJoin._RNIHash = '';
					tmpJoin._RNIFormOnly = false;
				}
			}

			return fCallback();
		};

		if (tmpIDList.length === 0)
		{
			return fDecorate({});
		}

		this.pict.HeadlightRestClient.getJSON(
			`ReportNamedInstances/FilteredTo/FBL~IDReportNamedInstance~INN~${tmpIDList.join(',')}/0/${tmpIDList.length}`,
			(pError, pBody) =>
			{
				if (pError)
				{
					this.pict.log.error(`Error resolving form definitions for the linked joins: ${pError}`);
					return fDecorate({});
				}

				const tmpRNIByID = {};
				const tmpRecords = Array.isArray(pBody) ? pBody : [];
				for (let i = 0; i < tmpRecords.length; i++)
				{
					tmpRNIByID[tmpRecords[i].IDReportNamedInstance] = tmpRecords[i];
				}

				return fDecorate(tmpRNIByID);
			}
		);
	}

	// ---- My Daily (first row of ReportNamedInstancesByProject) ----

	/**
	 * The join whose form Fieldbook web and iOS treat as the project's daily report.
	 *
	 * ReportNamedInstancesByProject issues no ORDER BY and its handler pins element [0] before
	 * alphabetizing the rest, so the daily is whichever row the IDProject index yields first —
	 * in practice the lowest IDReportNamedInstanceProjectJoin.
	 *
	 * @return {object?} the daily join record, or null when the project has no joins
	 */
	_getMyDailyJoin()
	{
		let tmpDaily = null;
		for (let i = 0; i < this._ProjectJoins.length; i++)
		{
			const tmpJoin = this._ProjectJoins[i];
			if (!tmpDaily || tmpJoin.IDReportNamedInstanceProjectJoin < tmpDaily.IDReportNamedInstanceProjectJoin)
			{
				tmpDaily = tmpJoin;
			}
		}
		return tmpDaily;
	}

	/** Joins parked on a non-positive IDReportNamedInstance by an interrupted `makeMyDaily` swap. */
	_getParkedJoins()
	{
		return this._ProjectJoins.filter((pJ) => !(pJ.IDReportNamedInstance > 0));
	}

	/**
	 * Write a single join's IDReportNamedInstance, sending only the writable columns.
	 *
	 * @param {object} pJoin
	 * @param {number} pIDReportNamedInstance
	 * @param {(pError?: Error) => void} fCallback
	 */
	_writeJoinReportNamedInstance(pJoin, pIDReportNamedInstance, fCallback)
	{
		const tmpPayload = {
			IDReportNamedInstanceProjectJoin: pJoin.IDReportNamedInstanceProjectJoin,
			IDReportNamedInstance: pIDReportNamedInstance,
			IDProject: pJoin.IDProject,
			IDCustomer: pJoin.IDCustomer,
			IDProgram: pJoin.IDProgram,
			Ordinal: pJoin.Ordinal
		};

		this.pict.HeadlightRestClient.updateEntity('ReportNamedInstanceProjectJoin', tmpPayload, fCallback);
	}

	/**
	 * Make the given join the project's My Daily report by swapping its IDReportNamedInstance with
	 * the one held by the lowest-ID join.
	 *
	 * AK_ReportNamedInstanceProjectJoin is UNIQUE on (IDReportNamedInstance, IDProject) and the table
	 * has no soft delete, so the two values cannot be exchanged directly — the lowest-ID row is
	 * parked on -1 for the duration of the swap.
	 *
	 * @param {number} pIDJoin
	 */
	makeMyDaily(pIDJoin)
	{
		if (!this._SelectedProject || this._IsRepairingDaily)
		{
			return;
		}

		const tmpTarget = this._ProjectJoins.find((pJ) => pJ.IDReportNamedInstanceProjectJoin === pIDJoin);
		if (!tmpTarget)
		{
			return;
		}

		const tmpCurrent = this._getMyDailyJoin();
		if (!tmpCurrent || tmpCurrent.IDReportNamedInstanceProjectJoin === tmpTarget.IDReportNamedInstanceProjectJoin)
		{
			this._showNotification('That form is already the My Daily report.', 'info');
			return;
		}

		if (tmpTarget._RNIFormOnly)
		{
			this._showNotification('Forms cannot be the My Daily report.', 'warning');
			return;
		}

		if (!(tmpTarget.IDReportNamedInstance > 0) || !(tmpCurrent.IDReportNamedInstance > 0))
		{
			this._showNotification('A previous swap left a join unassigned. Reload and resolve that first.', 'danger');
			return;
		}

		const tmpTargetName = tmpTarget._RNIName || `RNI ${tmpTarget.IDReportNamedInstance}`;
		const tmpCurrentName = tmpCurrent._RNIName || `RNI ${tmpCurrent.IDReportNamedInstance}`;
		const tmpPrompt = `"${tmpTargetName}" will become the My Daily report for this project, swapping places with "${tmpCurrentName}".`;

		const tmpModal = this.pict.views['Pict-Section-Modal'];
		if (tmpModal && typeof tmpModal.confirm === 'function')
		{
			tmpModal.confirm(tmpPrompt,
				{
					title: 'Change My Daily report?',
					confirmLabel: 'Make My Daily',
					cancelLabel: 'Cancel'
				}).then((pOk) =>
				{
					if (pOk)
					{
						this._executeMyDailySwap(tmpCurrent, tmpTarget, tmpTargetName);
					}
				});
		}
		else if ((typeof window !== 'undefined') && window.confirm(tmpPrompt))
		{
			this._executeMyDailySwap(tmpCurrent, tmpTarget, tmpTargetName);
		}
	}

	/**
	 * @param {object} pCurrentDailyJoin - the lowest-ID join, which holds the My Daily slot
	 * @param {object} pTargetJoin - the join whose form should become My Daily
	 * @param {string} pTargetName - display name for notifications
	 */
	_executeMyDailySwap(pCurrentDailyJoin, pTargetJoin, pTargetName)
	{
		const tmpIDProject = this._SelectedProject.IDProject;
		const tmpIDDailyJoin = pCurrentDailyJoin.IDReportNamedInstanceProjectJoin;
		const tmpTargetIDRNI = pTargetJoin.IDReportNamedInstance;
		const tmpDisplacedIDRNI = pCurrentDailyJoin.IDReportNamedInstance;

		this._IsRepairingDaily = true;
		const tmpRow = document.getElementById(`PF-Row-${pTargetJoin.IDReportNamedInstanceProjectJoin}`);
		if (tmpRow)
		{
			tmpRow.classList.add('is-loading');
		}

		const fFinish = (pError, pMessage, pType) =>
		{
			this._IsRepairingDaily = false;
			if (tmpRow)
			{
				tmpRow.classList.remove('is-loading');
			}
			if (pError)
			{
				this.pict.log.error(`Error changing My Daily on project ${tmpIDProject}: ${pError}`);
			}
			this._showNotification(pMessage, pType);
			this._loadJoinsForProject(tmpIDProject);
		};

		this._writeJoinReportNamedInstance(pCurrentDailyJoin, -1, (pParkError) =>
		{
			if (pParkError)
			{
				return fFinish(pParkError, 'Could not start the swap. Nothing was changed.', 'danger');
			}

			this._writeJoinReportNamedInstance(pTargetJoin, tmpDisplacedIDRNI, (pMoveError) =>
			{
				if (pMoveError)
				{
					return this._writeJoinReportNamedInstance(pCurrentDailyJoin, tmpDisplacedIDRNI, (pRollbackError) =>
					{
						if (pRollbackError)
						{
							return fFinish(pMoveError, `Swap failed and could not be rolled back. Join ${tmpIDDailyJoin} is unassigned and must be set back to form ${tmpDisplacedIDRNI}.`, 'danger');
						}
						return fFinish(pMoveError, 'Swap failed. The original order was restored.', 'danger');
					});
				}

				this._writeJoinReportNamedInstance(pCurrentDailyJoin, tmpTargetIDRNI, (pAssignError) =>
				{
					if (pAssignError)
					{
						return fFinish(pAssignError, `Swap incomplete. Join ${tmpIDDailyJoin} is unassigned and must be set to form ${tmpTargetIDRNI}.`, 'danger');
					}
					return fFinish(null, `"${pTargetName}" is now the My Daily report.`, 'success');
				});
			});
		});
	}

	_renderMyDailyStatus()
	{
		const tmpStatus = document.getElementById('PF-Daily-Status');
		if (!tmpStatus)
		{
			return;
		}

		if (this._IsLoadingJoins || this._ProjectJoins.length === 0)
		{
			tmpStatus.innerHTML = '';
			return;
		}

		const tmpParked = this._getParkedJoins();
		if (tmpParked.length > 0)
		{
			const tmpIDs = tmpParked.map((pJ) => pJ.IDReportNamedInstanceProjectJoin).join(', ');
			tmpStatus.innerHTML = /*html*/`
				<div class="notification is-danger is-soft py-2 px-3 mb-3">
					<span class="icon"><i class="fas fa-triangle-exclamation"></i></span>
					Join ${this._escapeHTML(tmpIDs)} has no form assigned, which means an earlier My Daily swap did not finish.
					Assign a form to it before changing the My Daily report again.
				</div>`;
			return;
		}

		const tmpDaily = this._getMyDailyJoin();
		if (!tmpDaily)
		{
			tmpStatus.innerHTML = '';
			return;
		}

		const tmpDailyName = this._escapeHTML(tmpDaily._RNIName || `RNI ${tmpDaily.IDReportNamedInstance}`);

		if (tmpDaily._RNIFormOnly)
		{
			tmpStatus.innerHTML = /*html*/`
				<div class="notification is-warning is-soft py-2 px-3 mb-3">
					<span class="icon"><i class="fas fa-triangle-exclamation"></i></span>
					<strong>${tmpDailyName}</strong> holds the My Daily slot but is a form, and forms cannot be a daily report.
					Use <em>Make My Daily</em> on a report row to fix it.
				</div>`;
			return;
		}

		tmpStatus.innerHTML = /*html*/`
			<div class="notification is-info is-soft py-2 px-3 mb-3">
				<span class="icon"><i class="fas fa-sun"></i></span>
				<strong>${tmpDailyName}</strong> is the My Daily report for this project.
			</div>`;
	}

	_loadAvailableRNIs()
	{
		this._IsLoadingRNIs = true;

		// Scope to the selected project's customer so super-admin users do not see
		// other customers' RNIs in the link-form dropdown.
		const tmpIDCustomer = this._getSelectedProjectIDCustomer();
		if (!tmpIDCustomer)
		{
			this._IsLoadingRNIs = false;
			this._AvailableRNIs = [];
			this._renderRNIPicker();
			return;
		}

		this.pict.HeadlightRestClient.getJSON(
			`ReportNamedInstances/FilteredTo/FBV~IDCustomer~EQ~${tmpIDCustomer}~FSF~Name~ASC~0/0/500`,
			(pError, pBody) =>
			{
				this._IsLoadingRNIs = false;

				if (pError)
				{
					this.pict.log.warn(`Could not load RNIs: ${pError}`);
					this._AvailableRNIs = [];
				}
				else
				{
					this._AvailableRNIs = Array.isArray(pBody) ? pBody : [];
				}

				this._renderRNIPicker();
			}
		);
	}

	// ---- Pull in default forms ----

	/**
	 * Order candidates so a report — never a FormOnly form — is written first.
	 *
	 * On a project with no joins the first row written takes the My Daily slot, and a best-effort
	 * guess at which report that should be is the lowest-numbered one: a customer's daily report is
	 * almost always among the first RNIs created for them. `makeMyDaily` repairs a wrong guess.
	 *
	 * @param {Array<object>} pRNIs
	 * @return {Array<object>} a new, deterministically ordered array
	 */
	_orderJoinCandidates(pRNIs)
	{
		return pRNIs.slice().sort((pA, pB) =>
		{
			const tmpAIsForm = (pA.Options && pA.Options.FormOnly) ? 1 : 0;
			const tmpBIsForm = (pB.Options && pB.Options.FormOnly) ? 1 : 0;

			if (tmpAIsForm !== tmpBIsForm)
			{
				return tmpAIsForm - tmpBIsForm;
			}

			return pA.IDReportNamedInstance - pB.IDReportNamedInstance;
		});
	}

	/**
	 * Link a set of forms to a project in one request.
	 *
	 * The bulk create endpoint walks the array with `async.eachSeries`, so join records are inserted
	 * strictly in the order given — which is what decides the My Daily report.
	 *
	 * @param {Array<object>} pPayloads - join records, in the order they should be created
	 * @param {(pError?: Error) => void} fCallback
	 */
	_createJoins(pPayloads, fCallback)
	{
		this.pict.HeadlightRestClient.postJSON('ReportNamedInstanceProjectJoins', pPayloads, fCallback);
	}

	pullInDefaultForms()
	{
		if (!this._SelectedProject) { return; }

		const tmpIDProject = this._SelectedProject.IDProject;
		const tmpBtn = document.getElementById('PF-Defaults-Button');
		if (tmpBtn) { tmpBtn.classList.add('is-loading'); }

		// Fetch customer-level default ReportNamedInstances directly.
		// We cannot use ReportNamedInstancesByProject here because that endpoint
		// returns the already-linked join records when any exist, rather than
		// the customer defaults we want to pull in.
		const tmpIDCustomer = this._getSelectedProjectIDCustomer();
		if (!tmpIDCustomer)
		{
			if (tmpBtn) { tmpBtn.classList.remove('is-loading'); }
			this._showNotification('Could not determine customer for this project.', 'danger');
			return;
		}

		this.pict.HeadlightRestClient.getJSON(
			`ReportNamedInstances/FilteredTo/FBV~IDCustomer~EQ~${tmpIDCustomer}~FBV~DefaultReport~EQ~1~FSF~IDReportNamedInstance~ASC~0/0/100`,
			(pError, pBody) =>
			{
				if (pError)
				{
					if (tmpBtn) { tmpBtn.classList.remove('is-loading'); }
					this.pict.log.error(`Error loading default forms: ${pError}`);
					this._showNotification('Error loading default forms. Please try again.', 'danger');
					return;
				}

				const tmpDefaults = Array.isArray(pBody) ? pBody : [];
				if (tmpDefaults.length === 0)
				{
					if (tmpBtn) { tmpBtn.classList.remove('is-loading'); }
					this._showNotification('No default forms found for this project.', 'info');
					return;
				}

				// Filter out forms already linked
				const tmpExistingIDs = new Set(this._ProjectJoins.map((pJ) => pJ.IDReportNamedInstance));
				const tmpToAdd = this._orderJoinCandidates(tmpDefaults.filter((pD) => !tmpExistingIDs.has(pD.IDReportNamedInstance)));

				if (tmpToAdd.length === 0)
				{
					if (tmpBtn) { tmpBtn.classList.remove('is-loading'); }
					this._showNotification('All default forms are already linked to this project.', 'info');
					return;
				}

				const tmpTotal = tmpToAdd.length;
				const tmpOrdinalBase = this._ProjectJoins.length;
				const tmpPayloads = tmpToAdd.map((pRNI, pIndex) =>
				{
					return {
						IDReportNamedInstance: pRNI.IDReportNamedInstance,
						IDProject: tmpIDProject,
						IDCustomer: tmpIDCustomer,
						IDProgram: 0,
						Ordinal: tmpOrdinalBase + pIndex
					};
				});

				this._createJoins(tmpPayloads, (pUpsertError) =>
				{
					if (tmpBtn) { tmpBtn.classList.remove('is-loading'); }

					if (pUpsertError)
					{
						this.pict.log.error(`Error linking default forms to project ${tmpIDProject}: ${pUpsertError}`);
						this._showNotification('Error linking default forms. Some may have been linked — check the list below.', 'danger');
					}
					else
					{
						this._showNotification(`Successfully linked ${tmpTotal} default form${tmpTotal > 1 ? 's' : ''}.`, 'success');
					}

					this._loadJoinsForProject(tmpIDProject);
				});
			}
		);
	}

	removeAllJoins()
	{
		if (!this._SelectedProject || this._ProjectJoins.length === 0) { return; }

		const tmpIDProject = this._SelectedProject.IDProject;
		const tmpBtn = document.getElementById('PF-RemoveAll-Button');
		if (tmpBtn) { tmpBtn.classList.add('is-loading'); }

		const tmpIDJoins = this._ProjectJoins.map((pJ) => pJ.IDReportNamedInstanceProjectJoin);
		const tmpTotal = tmpIDJoins.length;
		let tmpCompleted = 0;
		let tmpErrors = 0;

		// Order is irrelevant here — every row goes away, so nothing establishes a My Daily.
		const fOnDeleted = (pDelError) =>
		{
			if (pDelError)
			{
				tmpErrors++;
				this.pict.log.error(`Error deleting join: ${pDelError}`);
			}

			if (++tmpCompleted < tmpTotal)
			{
				return;
			}

			if (tmpBtn) { tmpBtn.classList.remove('is-loading'); }

			if (tmpErrors > 0)
			{
				this._showNotification(`Removed ${tmpTotal - tmpErrors} of ${tmpTotal} forms (${tmpErrors} failed).`, 'warning');
			}
			else
			{
				this._showNotification('All form links removed.', 'info');
			}

			this._loadJoinsForProject(tmpIDProject);
		};

		for (let i = 0; i < tmpTotal; i++)
		{
			this.pict.HeadlightRestClient.deleteEntity('ReportNamedInstanceProjectJoin', tmpIDJoins[i], fOnDeleted);
		}
	}

	// ---- Add / Remove join records ----

	addFormToProject()
	{
		if (!this._SelectedProject) { return; }

		const tmpIDRNI = this._selectedLinkIDRNI();
		if (!tmpIDRNI)
		{
			this._showNotification('Please select a form definition.', 'warning');
			return;
		}

		// Check for duplicate
		const tmpExisting = this._ProjectJoins.find((pJ) => pJ.IDReportNamedInstance === tmpIDRNI);
		if (tmpExisting)
		{
			this._showNotification('This form is already linked to this project.', 'warning');
			return;
		}

		const tmpIDCustomer = this._getSelectedProjectIDCustomer();
		if (!tmpIDCustomer)
		{
			this._showNotification('Could not determine customer for this project.', 'danger');
			return;
		}

		// Seed-on-first-customize: while this project is inheriting the customer default forms (no joins of its
		// own), adding one form would break inheritance in the field apps and drop the rest. Materialize the
		// defaults first and include the selected form, so nothing disappears. (Mirrors "Customize Defaults" and
		// the observation-type editor's "+ Add".)
		if (this._ProjectJoins.length === 0)
		{
			this._addFormWithDefaults(tmpIDRNI, tmpIDCustomer);
			return;
		}

		const tmpPayload = {
			IDReportNamedInstance: tmpIDRNI,
			IDProject: this._SelectedProject.IDProject,
			IDCustomer: tmpIDCustomer,
			IDProgram: 0,
			Ordinal: this._ProjectJoins.length
		};

		const tmpAddBtn = document.getElementById('PF-Add-Button');
		if (tmpAddBtn) { tmpAddBtn.classList.add('is-loading'); }

		this.pict.HeadlightRestClient.createEntity('ReportNamedInstanceProjectJoin', tmpPayload, (pError, pBody) =>
		{
			if (tmpAddBtn) { tmpAddBtn.classList.remove('is-loading'); }

			if (pError)
			{
				this.pict.log.error(`Error creating project join: ${pError}`);
				this._showNotification('Error linking form. Please try again.', 'danger');
				return;
			}

			this._showNotification('Form linked successfully!', 'success');
			this._clearRNIPicker();
			this._loadJoinsForProject(this._SelectedProject.IDProject);
		});
	}

	/**
	 * Add a form to a project that is still inheriting the customer defaults: materialize the customer default
	 * forms FIRST, then include the explicitly-selected form, in ONE bulk create — so starting to customize
	 * doesn't silently drop the inherited set. The defaults keep their inherited order (preserving which report
	 * becomes My Daily); the selected form is appended only when it is not already one of the defaults.
	 *
	 * @param {number} pIDReportNamedInstance - the form the user picked to add
	 * @param {number} pIDCustomer - the project's customer
	 */
	_addFormWithDefaults(pIDReportNamedInstance, pIDCustomer)
	{
		const tmpIDProject = this._SelectedProject.IDProject;
		const tmpAddBtn = document.getElementById('PF-Add-Button');
		if (tmpAddBtn) { tmpAddBtn.classList.add('is-loading'); }

		this.pict.HeadlightRestClient.getJSON(
			`ReportNamedInstances/FilteredTo/FBV~IDCustomer~EQ~${pIDCustomer}~FBV~DefaultReport~EQ~1~FSF~IDReportNamedInstance~ASC~0/0/100`,
			(pError, pBody) =>
			{
				// Best-effort: if the defaults can't be fetched, still add the one selected form (never block it).
				const tmpDefaults = (!pError && Array.isArray(pBody)) ? this._orderJoinCandidates(pBody) : [];
				const tmpIDs = tmpDefaults.map((pRNI) => pRNI.IDReportNamedInstance);
				if (tmpIDs.indexOf(pIDReportNamedInstance) < 0) { tmpIDs.push(pIDReportNamedInstance); }

				const tmpPayloads = tmpIDs.map((pID, pIndex) =>
				{
					return {
						IDReportNamedInstance: pID,
						IDProject: tmpIDProject,
						IDCustomer: pIDCustomer,
						IDProgram: 0,
						Ordinal: pIndex
					};
				});

				this._createJoins(tmpPayloads, (pUpsertError) =>
				{
					if (tmpAddBtn) { tmpAddBtn.classList.remove('is-loading'); }

					if (pUpsertError)
					{
						this.pict.log.error(`Error linking form (with defaults) to project ${tmpIDProject}: ${pUpsertError}`);
						this._showNotification('Error linking form. Please try again.', 'danger');
						return;
					}

					this._showNotification((tmpDefaults.length > 0)
						? 'Form linked — the customer default forms were pulled in too, so nothing was lost.'
						: 'Form linked successfully!', 'success');
					this._clearRNIPicker();
					this._loadJoinsForProject(tmpIDProject);
				});
			}
		);
	}

	removeJoin(pIDJoin)
	{
		if (!pIDJoin) { return; }

		const tmpRow = document.getElementById(`PF-Row-${pIDJoin}`);
		if (tmpRow) { tmpRow.classList.add('is-loading'); }

		this.pict.HeadlightRestClient.deleteEntity('ReportNamedInstanceProjectJoin', pIDJoin,
			(pDelError) =>
			{
				if (tmpRow) { tmpRow.classList.remove('is-loading'); }

				if (pDelError)
				{
					this.pict.log.error(`Error deleting join: ${pDelError}`);
					this._showNotification('Error removing form link.', 'danger');
					return;
				}

				this._showNotification('Form link removed.', 'info');
				this._loadJoinsForProject(this._SelectedProject.IDProject);
			}
		);
	}

	// ---- L5 / LIMS Concierge gate ----

	_isL5User()
	{
		return !!(this.AppData && this.AppData.CurrentUser && Number(this.AppData.CurrentUser.UserRoleIndex) >= 5 && this.AppData.CurrentUser.CustomerID == 1);
	}

	/** The injected materials FORM_GROUPS map (empty object when the host did not provide it). */
	_formGroups()
	{
		return (this.options && this.options.FormGroups) || {};
	}

	/** Whether the bulk-add feature is available (the host injected a non-empty FormGroups map). */
	_hasFormGroups()
	{
		const tmpGroups = this._formGroups();
		return !!tmpGroups && (Object.keys(tmpGroups).length > 0);
	}

	/** Injected LIMS-form predicate (materials-form-manager.isLIMSForm); a no-op (false) when not provided. */
	_isLIMSForm(pHashOrName)
	{
		return (this.options && (typeof this.options.IsLIMSForm === 'function')) ? !!this.options.IsLIMSForm(pHashOrName) : false;
	}

	/**
	 * Show or hide the L5-only sections (LIMS Concierge box, Bulk Add Forms button)
	 * based on the current user's role and whether a project is selected.
	 */
	_refreshL5Sections()
	{
		const tmpL5 = this._isL5User();
		const tmpHasSelection = !!this._SelectedProject;
		const tmpConciergeBox = document.getElementById('PF-LIMS-Concierge-Section');
		const tmpBulkButton = document.getElementById('PF-BulkAdd-Button');
		if (tmpConciergeBox)
		{
			tmpConciergeBox.classList.toggle('is-hidden', !(tmpL5 && tmpHasSelection));
		}
		if (tmpBulkButton)
		{
			// Bulk-add also requires the injected FormGroups (materials-form-manager); hidden without them.
			tmpBulkButton.classList.toggle('is-hidden', !(tmpL5 && tmpHasSelection && this._hasFormGroups()));
		}
	}

	/**
	 * Read the project-level ConciergeDefaults/Sample config for the current project
	 * and update the LIMS Concierge section UI.
	 *
	 * Uses POST /Configuration/Get with an explicit Project LevelContext (rather than the
	 * IDProject GET shortcut, which is User-level cascade and would not surface what's
	 * actually stored at Project level).
	 *
	 * @param {number} pIDProject
	 */
	_loadLIMSConciergeConfig(pIDProject)
	{
		this._IsLoadingConciergeConfig = true;
		this._renderLIMSConciergeStatus();

		this.pict.HeadlightRestClient.postJSON(
			'Configuration/Get',
			{
				NamespaceKey: 'ConciergeDefaults',
				NamespaceValue: 'Sample',
				LevelContext: 'Project',
				LevelContextID: pIDProject,
			},
			(pError, pBody) =>
			{
				this._IsLoadingConciergeConfig = false;
				if (pError)
				{
					this.pict.log.warn(`Could not load ConciergeDefaults config for project ${pIDProject}: ${pError}`);
					this._ConciergeConfig = {};
				}
				else if (pBody && typeof pBody === 'object' && pBody.Config && typeof pBody.Config === 'object')
				{
					this._ConciergeConfig = pBody.Config;
				}
				else if (pBody && typeof pBody === 'object')
				{
					this._ConciergeConfig = pBody;
				}
				else
				{
					this._ConciergeConfig = {};
				}
				this._renderLIMSConciergeStatus();
			}
		);
	}

	/**
	 * Read the current config, flip Enabled, and write back. Configuration/Put is full-key
	 * overwrite — preserve everything else (Hashes, CreatePerLab, etc.) by reading first.
	 */
	onLIMSConciergeToggle()
	{
		if (!this._SelectedProject) { return; }
		if (!this._isL5User())
		{
			this._showNotification('Only platform administrators can change this setting.', 'warning');
			return;
		}
		const tmpSwitch = document.getElementById('PF-LIMS-Concierge-Toggle');
		if (!tmpSwitch) { return; }
		// The switch is a button, so it carries no implicit checked state — it reports the state it is
		// currently painting via aria-checked, and a click asks for the opposite.
		const tmpDesired = (tmpSwitch.getAttribute('aria-checked') !== 'true');
		const tmpIDProject = this._SelectedProject.IDProject;

		// Paint the requested state immediately, and lock the control while the write is in flight.
		this._paintConciergeSwitch(tmpDesired);
		tmpSwitch.disabled = true;

		const tmpExisting = (this._ConciergeConfig && typeof this._ConciergeConfig === 'object') ? this._ConciergeConfig : {};
		const tmpNewConfig = Object.assign({}, tmpExisting, { Enabled: tmpDesired });

		this.pict.HeadlightRestClient.postJSON(
			`Configuration/Put/ConciergeDefaults/Sample/Project/${tmpIDProject}`,
			{ Config: tmpNewConfig },
			(pError) =>
			{
				tmpSwitch.disabled = false;
				if (pError)
				{
					this.pict.log.error(`Error writing ConciergeDefaults config for project ${tmpIDProject}: ${pError}`);
					this._showNotification('Could not save LIMS Concierge setting. Configuration/Put requires platform admin.', 'danger');
					// Revert the switch to the previously known state.
					this._paintConciergeSwitch(!tmpDesired);
					return;
				}
				this._ConciergeConfig = tmpNewConfig;
				this._showNotification(`LIMS Concierge ${tmpDesired ? 'enabled' : 'disabled'} for this project.`, 'success');
				this._renderLIMSConciergeStatus();
			}
		);
	}

	/**
	 * Paint the concierge switch for a boolean state.
	 *
	 * The control is a `.button.is-toggle` (headlight-styles), which shows the "on" icon upright when
	 * `.is-active` and rotates it 180deg otherwise; `aria-checked` carries the same state to assistive
	 * tech and is what `onLIMSConciergeToggle` reads back. Deliberately no text label — the wording
	 * used to flip between "Enabled"/"Disabled" on the control itself, which read as a command rather
	 * than a state. The status line below the title is the readout now.
	 *
	 * @param {boolean} pEnabled
	 */
	_paintConciergeSwitch(pEnabled)
	{
		const tmpSwitch = document.getElementById('PF-LIMS-Concierge-Toggle');
		if (!tmpSwitch) { return; }
		tmpSwitch.classList.toggle('is-active', !!pEnabled);
		tmpSwitch.setAttribute('aria-checked', pEnabled ? 'true' : 'false');
	}

	_renderLIMSConciergeStatus()
	{
		const tmpSection = document.getElementById('PF-LIMS-Concierge-Section');
		if (!tmpSection) { return; }
		const tmpStatusText = document.getElementById('PF-LIMS-Concierge-Status');
		const tmpToggle = document.getElementById('PF-LIMS-Concierge-Toggle');
		const tmpWarning = document.getElementById('PF-LIMS-Concierge-Warning');
		if (!tmpStatusText || !tmpToggle || !tmpWarning) { return; }

		if (this._IsLoadingConciergeConfig)
		{
			tmpStatusText.textContent = 'Loading…';
			tmpToggle.disabled = true;
			tmpWarning.classList.add('is-hidden');
			return;
		}

		const tmpEnabled = this._ConciergeConfig && this._ConciergeConfig.Enabled === true;
		const tmpExplicit = this._ConciergeConfig && typeof this._ConciergeConfig.Enabled === 'boolean';
		this._paintConciergeSwitch(!!tmpEnabled);
		tmpToggle.disabled = false;
		if (tmpExplicit)
		{
			tmpStatusText.textContent = `Project-level setting: ${tmpEnabled ? 'Enabled' : 'Disabled'}.`;
		}
		else
		{
			tmpStatusText.textContent = 'Not explicitly set; disabled.';
		}

		// Warn if Enabled=true but no LIMS forms are joined.
		const tmpHasLIMSJoin = this._ProjectJoins.some((pJ) => this._isLIMSForm(pJ._RNIHash || '') || this._isLIMSForm(pJ._RNIName || ''));
		if (tmpEnabled && !tmpHasLIMSJoin && !this._IsLoadingJoins)
		{
			tmpWarning.classList.remove('is-hidden');
		}
		else
		{
			tmpWarning.classList.add('is-hidden');
		}
	}

	// ---- Bulk-add modal (form-group driven) ----

	openBulkAddModal()
	{
		if (!this._SelectedProject) { return; }
		if (!this._isL5User())
		{
			this._showNotification('Only platform administrators can use bulk add.', 'warning');
			return;
		}
		const tmpModal = document.getElementById('PF-BulkAdd-Modal');
		if (!tmpModal) { return; }
		tmpModal.classList.add('is-active');
		this._renderBulkAddGroupOptions();
		this._renderBulkAddCandidateList();
	}

	closeBulkAddModal()
	{
		const tmpModal = document.getElementById('PF-BulkAdd-Modal');
		if (tmpModal) { tmpModal.classList.remove('is-active'); }
	}

	onBulkAddGroupChange()
	{
		const tmpSelect = document.getElementById('PF-BulkAdd-Group-Select');
		if (!tmpSelect) { return; }
		this._BulkAddSelectedGroup = tmpSelect.value || null;
		this._renderBulkAddCandidateList();
	}

	toggleAllBulkAddRows(pCheck)
	{
		const tmpInputs = document.querySelectorAll('#PF-BulkAdd-List input[type="checkbox"]');
		tmpInputs.forEach((pInput) =>
		{
			pInput.checked = !!pCheck;
		});
	}

	_renderBulkAddGroupOptions()
	{
		const tmpSelect = document.getElementById('PF-BulkAdd-Group-Select');
		const tmpDescription = document.getElementById('PF-BulkAdd-Group-Description');
		if (!tmpSelect) { return; }
		const tmpGroups = this._formGroups();
		const tmpKeys = Object.keys(tmpGroups);
		if (tmpKeys.length === 0)
		{
			tmpSelect.innerHTML = '<option value="">(no groups defined)</option>';
			if (tmpDescription) { tmpDescription.textContent = ''; }
			return;
		}
		if (!this._BulkAddSelectedGroup || !tmpGroups[this._BulkAddSelectedGroup])
		{
			this._BulkAddSelectedGroup = tmpKeys[0];
		}
		let tmpHTML = '';
		for (const tmpKey of tmpKeys)
		{
			const tmpGroup = tmpGroups[tmpKey];
			const tmpSelected = tmpKey === this._BulkAddSelectedGroup ? ' selected' : '';
			tmpHTML += `<option value="${this._escapeHTML(tmpKey)}"${tmpSelected}>${this._escapeHTML(tmpGroup.Name || tmpKey)}</option>`;
		}
		tmpSelect.innerHTML = tmpHTML;
		if (tmpDescription)
		{
			const tmpActive = tmpGroups[this._BulkAddSelectedGroup];
			tmpDescription.textContent = tmpActive ? (tmpActive.Description || '') : '';
		}
	}

	_renderBulkAddCandidateList()
	{
		const tmpList = document.getElementById('PF-BulkAdd-List');
		if (!tmpList) { return; }
		const tmpGroups = this._formGroups();
		const tmpGroup = this._BulkAddSelectedGroup ? tmpGroups[this._BulkAddSelectedGroup] : null;
		if (!tmpGroup || typeof tmpGroup.getMembers !== 'function')
		{
			tmpList.innerHTML = '<p class="has-text-grey-light">Select a form group to see candidates.</p>';
			this._BulkAddCandidates = [];
			return;
		}
		const tmpExistingIDs = new Set(this._ProjectJoins.map((pJ) => pJ.IDReportNamedInstance));
		// Pass ConciergeConfig so the LIMS group unions in any RNI whose Hash is listed in
		// ConciergeDefaults/Sample.Hashes — i.e. the "extra forms" the materials API auto-creates
		// even when the GUID doesn't match the LIMS_GUID_PATTERNS heuristic.
		const tmpGroupMembers = tmpGroup.getMembers(this._AvailableRNIs, { ConciergeConfig: this._ConciergeConfig });
		const tmpCandidates = tmpGroupMembers.filter((pRNI) => !tmpExistingIDs.has(pRNI.IDReportNamedInstance));
		this._BulkAddCandidates = tmpCandidates;
		if (tmpCandidates.length === 0)
		{
			tmpList.innerHTML = '<p class="has-text-grey-light">No matching forms outside this project.</p>';
			return;
		}
		let tmpHTML = '';
		for (const tmpRNI of tmpCandidates)
		{
			const tmpLabel = tmpRNI.Name ? `${tmpRNI.Name} (${tmpRNI.Hash || tmpRNI.IDReportNamedInstance})` : `RNI ${tmpRNI.IDReportNamedInstance}`;
			tmpHTML += /*html*/`
				<label class="checkbox is-block mb-1">
					<input type="checkbox" data-id="${tmpRNI.IDReportNamedInstance}" checked />
					${this._escapeHTML(tmpLabel)}
					<small class="has-text-grey ml-2">${this._escapeHTML(tmpRNI.GUIDReportNamedInstance || '')}</small>
				</label>`;
		}
		tmpList.innerHTML = tmpHTML;
	}

	confirmBulkAdd()
	{
		if (!this._SelectedProject) { return; }
		const tmpInputs = Array.from(document.querySelectorAll('#PF-BulkAdd-List input[type="checkbox"]:checked'));
		if (tmpInputs.length === 0)
		{
			this._showNotification('No forms selected.', 'warning');
			return;
		}
		const tmpSelectedIDs = tmpInputs.map((pInput) => parseInt(pInput.getAttribute('data-id'), 10)).filter((id) => id > 0);
		if (tmpSelectedIDs.length === 0)
		{
			this._showNotification('No valid form IDs found in selection.', 'warning');
			return;
		}

		const tmpConfirmButton = document.getElementById('PF-BulkAdd-Confirm-Button');
		if (tmpConfirmButton) { tmpConfirmButton.classList.add('is-loading'); }

		const tmpIDProject = this._SelectedProject.IDProject;
		const tmpIDCustomer = this._getSelectedProjectIDCustomer();
		if (!tmpIDCustomer)
		{
			if (tmpConfirmButton) { tmpConfirmButton.classList.remove('is-loading'); }
			this._showNotification('Could not determine customer for this project.', 'danger');
			return;
		}

		const tmpSelected = new Set(tmpSelectedIDs);
		const tmpOrdered = this._orderJoinCandidates(this._BulkAddCandidates.filter((pRNI) => tmpSelected.has(pRNI.IDReportNamedInstance)));
		const tmpTotal = tmpOrdered.length;
		const tmpOrdinalBase = this._ProjectJoins.length;
		const tmpPayloads = tmpOrdered.map((pRNI, pIndex) =>
		{
			return {
				IDReportNamedInstance: pRNI.IDReportNamedInstance,
				IDProject: tmpIDProject,
				IDCustomer: tmpIDCustomer,
				IDProgram: 0,
				Ordinal: tmpOrdinalBase + pIndex,
			};
		});

		this._createJoins(tmpPayloads, (pUpsertError) =>
		{
			if (tmpConfirmButton) { tmpConfirmButton.classList.remove('is-loading'); }
			if (pUpsertError)
			{
				this.pict.log.error(`Error linking forms to project ${tmpIDProject}: ${pUpsertError}`);
				this._showNotification('Error linking forms. Some may have been linked — check the list below.', 'danger');
			}
			else
			{
				this._showNotification(`Linked ${tmpTotal} form${tmpTotal > 1 ? 's' : ''}.`, 'success');
			}
			this.closeBulkAddModal();
			this._loadJoinsForProject(tmpIDProject);
		});
	}

	// ---- Rendering helpers ----

	_renderProjectList()
	{
		const tmpListBody = document.getElementById('PF-Project-List-Body');
		if (!tmpListBody) { return; }

		if (this._IsLoadingProjects)
		{
			tmpListBody.innerHTML = `
				<tr>
					<td colspan="3" class="has-text-centered">
						<span class="icon"><i class="fas fa-spinner fa-spin"></i></span>
						Loading projects...
					</td>
				</tr>`;
			return;
		}

		const tmpHasSearch = !!(this._ProjectSearchTerm && this._ProjectSearchTerm.trim());

		if (this._Projects.length === 0)
		{
			tmpListBody.innerHTML = `
				<tr>
					<td colspan="3" class="has-text-centered has-text-grey-light">
						<span class="icon"><i class="fas fa-folder-open"></i></span>
						${tmpHasSearch ? 'No projects match your search.' : 'No projects found.'}
					</td>
				</tr>`;
			return;
		}

		let tmpHTML = '';
		for (let i = 0; i < this._Projects.length; i++)
		{
			const tmpP = this._Projects[i];
			const tmpIsSelected = this._SelectedProject && this._SelectedProject.IDProject === tmpP.IDProject;
			tmpHTML += `
				<tr class="pf-project-row${tmpIsSelected ? ' is-selected' : ''}"
					onclick="_Pict.views.ReportNamedInstanceProjectForms.selectProject(${tmpP.IDProject})">
					<td>${this._escapeHTML(tmpP.ExternalProjectID || '—')}</td>
					<td>
						<strong>${this._escapeHTML(tmpP.Name || '')}</strong>
						${tmpP.Description ? `<br><small class="has-text-grey">${this._escapeHTML(tmpP.Description)}</small>` : ''}
					</td>
					<td><small class="has-text-grey">${this._escapeHTML(tmpP.Status || '')}</small></td>
				</tr>`;
		}
		tmpListBody.innerHTML = tmpHTML;
	}

	_renderSelectedProjectHeader()
	{
		const tmpHeader = document.getElementById('PF-Selected-Project-Header');
		const tmpDetail = document.getElementById('PF-Detail-Panel');
		const tmpEmptyDetail = document.getElementById('PF-Empty-Detail');

		if (!tmpHeader || !tmpDetail || !tmpEmptyDetail) { return; }

		if (!this._SelectedProject)
		{
			tmpDetail.classList.add('is-hidden');
			tmpEmptyDetail.classList.remove('is-hidden');
			return;
		}

		tmpDetail.classList.remove('is-hidden');
		tmpEmptyDetail.classList.add('is-hidden');

		tmpHeader.innerHTML = `
			<div class="level">
				<div class="level-left">
					<div class="level-item">
						<div>
							<p class="title is-5 mb-1">
								<span class="icon"><i class="fas fa-diagram-project"></i></span>
								${this._escapeHTML(this._SelectedProject.Name || '')}
							</p>
							<p class="subtitle is-6 has-text-grey mb-0">
								ID: ${this._SelectedProject.IDProject}
								${this._SelectedProject.Description ? ` | ${this._escapeHTML(this._SelectedProject.Description)}` : ''}
							</p>
						</div>
					</div>
				</div>
				<div class="level-right">
					<div class="level-item">
						<button class="button is-small" onclick="_Pict.views.ReportNamedInstanceProjectForms.deselectProject()">
							<span class="icon"><i class="fas fa-xmark"></i></span>
							<span>Close</span>
						</button>
					</div>
				</div>
			</div>`;
	}

	_renderJoinsTable()
	{
		const tmpTableBody = document.getElementById('PF-Joins-Body');
		if (!tmpTableBody) { return; }

		this._renderMyDailyStatus();

		if (this._IsLoadingJoins)
		{
			tmpTableBody.innerHTML = `
				<tr>
					<td colspan="6" class="has-text-centered">
						<span class="icon"><i class="fas fa-spinner fa-spin"></i></span>
						Loading linked forms...
					</td>
				</tr>`;
			return;
		}

		if (this._ProjectJoins.length === 0)
		{
			tmpTableBody.innerHTML = `
				<tr>
					<td colspan="6" class="has-text-centered has-text-grey-light">
						<span class="icon"><i class="fas fa-link-slash"></i></span>
						No form definitions linked to this project.
					</td>
				</tr>`;
			return;
		}

		// Sort by Ordinal, falling back to join ID so equal Ordinals render in a stable order
		this._ProjectJoins.sort((pA, pB) =>
		{
			return ((pA.Ordinal || 0) - (pB.Ordinal || 0)) || (pA.IDReportNamedInstanceProjectJoin - pB.IDReportNamedInstanceProjectJoin);
		});

		const tmpDaily = this._getMyDailyJoin();
		const tmpIDDailyJoin = tmpDaily ? tmpDaily.IDReportNamedInstanceProjectJoin : 0;

		let tmpHTML = '';
		for (let i = 0; i < this._ProjectJoins.length; i++)
		{
			const tmpJoin = this._ProjectJoins[i];
			const tmpIsDaily = tmpJoin.IDReportNamedInstanceProjectJoin === tmpIDDailyJoin;

			let tmpTypeCell;
			if (tmpIsDaily)
			{
				tmpTypeCell = `
					<span class="tag is-info" title="Join ${tmpJoin.IDReportNamedInstanceProjectJoin} is the lowest for this project, so ReportNamedInstancesByProject returns it first">
						<span class="icon"><i class="fas fa-sun"></i></span>
						<span>Report (daily)</span>
					</span>`;
			}
			else if (tmpJoin._RNIFormOnly)
			{
				tmpTypeCell = '<span class="tag is-light" title="Options.FormOnly is set, so this cannot be the daily report">Form</span>';
			}
			else
			{
				tmpTypeCell = '<span class="tag is-light">Report</span>';
			}

			const tmpMakeDailyButton = (tmpIsDaily || tmpJoin._RNIFormOnly) ? '' : `
						<button class="button is-small is-info is-outlined mr-2"
							title="Make this the daily report Fieldbook offers first"
							onclick="_Pict.views.ReportNamedInstanceProjectForms.makeMyDaily(${tmpJoin.IDReportNamedInstanceProjectJoin})">
							<span class="icon"><i class="fas fa-sun"></i></span>
							<span>Make Daily</span>
						</button>`;

			tmpHTML += `
				<tr id="PF-Row-${tmpJoin.IDReportNamedInstanceProjectJoin}" draggable="true"
					class="${tmpIsDaily ? 'pf-daily-row' : ''}"
					data-join-id="${tmpJoin.IDReportNamedInstanceProjectJoin}" data-ordinal="${tmpJoin.Ordinal || 0}">
					<td class="pf-drag-handle"><span class="icon has-text-grey-light"><i class="fas fa-grip-vertical"></i></span></td>
					<td>${tmpJoin.IDReportNamedInstance}</td>
					<td>
						<strong>${this._escapeHTML(tmpJoin._RNIName || '')}</strong>
						${tmpJoin._RNIHash ? ` <code>${this._escapeHTML(tmpJoin._RNIHash)}</code>` : ''}
					</td>
					<td>${tmpTypeCell}</td>
					<td class="pf-ordinal-cell">${tmpJoin.Ordinal || 0}</td>
					<td class="has-text-right">
						${tmpMakeDailyButton}
						<button class="button is-small is-danger is-outlined"
							onclick="_Pict.views.ReportNamedInstanceProjectForms.removeJoin(${tmpJoin.IDReportNamedInstanceProjectJoin})">
							<span class="icon"><i class="fas fa-trash-can"></i></span>
							<span>Remove</span>
						</button>
					</td>
				</tr>`;
		}
		tmpTableBody.innerHTML = tmpHTML;
		this._initDragDrop();
	}

	_initDragDrop()
	{
		const tmpTableBody = document.getElementById('PF-Joins-Body');
		if (!tmpTableBody) { return; }

		let tmpDragRow = null;

		// Create a placeholder row that shows where the drop will land
		const tmpPlaceholder = document.createElement('tr');
		tmpPlaceholder.className = 'pf-drag-placeholder';
		tmpPlaceholder.innerHTML = '<td colspan="6"></td>';

		const tmpRemovePlaceholder = () =>
		{
			if (tmpPlaceholder.parentNode) { tmpPlaceholder.parentNode.removeChild(tmpPlaceholder); }
		};

		tmpTableBody.querySelectorAll('tr[draggable]').forEach((pRow) =>
		{
			pRow.addEventListener('dragstart', (pEvent) =>
			{
				tmpDragRow = pRow;
				pRow.classList.add('pf-dragging');
				pEvent.dataTransfer.effectAllowed = 'move';
			});

			pRow.addEventListener('dragend', () =>
			{
				if (tmpDragRow) { tmpDragRow.classList.remove('pf-dragging'); }
				tmpDragRow = null;
				tmpRemovePlaceholder();
			});

			pRow.addEventListener('dragover', (pEvent) =>
			{
				pEvent.preventDefault();
				pEvent.dataTransfer.dropEffect = 'move';
				if (!tmpDragRow || pRow === tmpDragRow) { return; }

				// Detect top or bottom half of the hovered row
				const tmpRect = pRow.getBoundingClientRect();
				const tmpMidpoint = tmpRect.top + (tmpRect.height / 2);
				const tmpIsAbove = pEvent.clientY < tmpMidpoint;

				if (tmpIsAbove)
				{
					pRow.parentNode.insertBefore(tmpPlaceholder, pRow);
				}
				else
				{
					pRow.parentNode.insertBefore(tmpPlaceholder, pRow.nextSibling);
				}
			});

			pRow.addEventListener('drop', (pEvent) =>
			{
				pEvent.preventDefault();
				if (!tmpDragRow || pRow === tmpDragRow) { tmpRemovePlaceholder(); return; }

				// Insert the dragged row where the placeholder is
				tmpPlaceholder.parentNode.insertBefore(tmpDragRow, tmpPlaceholder);
				tmpRemovePlaceholder();

				this._persistOrdinals();
			});
		});
	}

	_persistOrdinals()
	{
		const tmpTableBody = document.getElementById('PF-Joins-Body');
		if (!tmpTableBody) { return; }

		const tmpRows = Array.from(tmpTableBody.querySelectorAll('tr[data-join-id]'));
		const tmpUpdates = [];

		for (let i = 0; i < tmpRows.length; i++)
		{
			const tmpIDJoin = parseInt(tmpRows[i].getAttribute('data-join-id'), 10);
			const tmpJoin = this._ProjectJoins.find((pJ) => pJ.IDReportNamedInstanceProjectJoin === tmpIDJoin);

			if (tmpJoin)
			{
				tmpJoin.Ordinal = i;
			}
			tmpRows[i].setAttribute('data-ordinal', i);
			const tmpOrdinalCell = tmpRows[i].querySelector('.pf-ordinal-cell');
			if (tmpOrdinalCell)
			{
				tmpOrdinalCell.textContent = i;
			}

			tmpUpdates.push({ IDReportNamedInstanceProjectJoin: tmpIDJoin, Ordinal: i });
		}

		this.pict.HeadlightRestClient.putJSON(
			'ReportNamedInstanceProjectJoins',
			tmpUpdates,
			(pError) =>
			{
				if (pError)
				{
					this.pict.log.error(`Error updating ordinals: ${pError}`);
					this._showNotification('Error saving new order. Please try again.', 'warning');
				}
			}
		);
	}

	/** The pict-section-picker provider, or null when the host app did not register it. */
	_pickerProvider()
	{
		const tmpProvider = this.pict.providers && this.pict.providers['Pict-Section-Picker'];
		return (tmpProvider && (typeof tmpProvider.createPicker === 'function')) ? tmpProvider : null;
	}

	/** The mounted picker view, or null before it has been created. */
	_rniPickerView()
	{
		return (this.pict.views && this.pict.views[RNI_PICKER_HASH]) || null;
	}

	/** The IDReportNamedInstance currently chosen in the picker (0 when empty). */
	_selectedLinkIDRNI()
	{
		const tmpView = this._rniPickerView();
		if (!tmpView || (typeof tmpView.getValue !== 'function')) { return 0; }
		const tmpID = parseInt(tmpView.getValue(), 10);
		return Number.isFinite(tmpID) && tmpID > 0 ? tmpID : 0;
	}

	/** Empty the picker's selection (after a successful link, or when the project is deselected). */
	_clearRNIPicker()
	{
		const tmpView = this._rniPickerView();
		if (tmpView && (typeof tmpView.setValue === 'function')) { tmpView.setValue(null); }
	}

	/**
	 * Mount — or re-configure and repaint — the searchable form-definition picker.
	 *
	 * `_loadAvailableRNIs` already pulls the customer's whole RNI set, so this is a static-Options
	 * picker: search filters the loaded list client-side with no further requests. It replaces a
	 * native <select> that listed every form in one unfiltered, viewport-height dropdown.
	 *
	 * createPicker reconfigures and reuses an existing view of the same hash, so calling this on
	 * every load/repaint refreshes the option list in place.
	 */
	_renderRNIPicker()
	{
		const tmpProvider = this._pickerProvider();
		if (!tmpProvider) { return; }
		if (!document.getElementById(RNI_PICKER_HASH)) { return; }

		const tmpOptions = this._IsLoadingRNIs ? [] : this._AvailableRNIs.map((pRNI) =>
			({
				Value: pRNI.IDReportNamedInstance,
				Text: pRNI.Name || `RNI ${pRNI.IDReportNamedInstance}`,
				// The hash rides as a badge rather than inside the label, so searching "Name" is not
				// diluted by hash text while the hash still disambiguates same-named forms.
				Tag: pRNI.Hash || String(pRNI.IDReportNamedInstance),
			}));

		const tmpView = tmpProvider.createPicker(RNI_PICKER_HASH,
			{
				DestinationAddress: `#${RNI_PICKER_HASH}`,
				ValueAddress: RNI_PICKER_VALUE_ADDRESS,
				Searchable: true,
				AllowClear: true,
				ClearLabel: 'None',
				TagLast: true,
				Placeholder: this._IsLoadingRNIs ? 'Loading forms…' : 'Search form definitions…',
				Options: tmpOptions,
			});
		if (tmpView && (typeof tmpView.render === 'function')) { tmpView.render(); }
	}

	onProjectSearch(pValue)
	{
		this._ProjectSearchTerm = pValue || '';

		if (this._ProjectSearchDebounceTimer)
		{
			clearTimeout(this._ProjectSearchDebounceTimer);
		}
		this._ProjectSearchDebounceTimer = setTimeout(() =>
		{
			this._ProjectSearchDebounceTimer = null;
			this._loadProjects();
		}, 250);
	}

	_escapeHTML(pStr)
	{
		if (!pStr) { return ''; }
		return pStr.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');
	}

	_showNotification(pMessage, pType)
	{
		const tmpContainer = document.getElementById('PF-Notification-Area');
		if (tmpContainer)
		{
			tmpContainer.innerHTML = /*html*/`
				<div class="notification is-${pType} is-soft">
					<button class="delete" onclick="this.parentElement.remove()"></button>
					${pMessage}
				</div>`;
			setTimeout(() =>
			{
				tmpContainer.innerHTML = '';
			}, 4000);
		}
	}

	onAfterRender()
	{
		super.onAfterRender();

		const tmpSearchInput = document.getElementById('PF-Project-Search');
		if (tmpSearchInput)
		{
			tmpSearchInput.addEventListener('input', (pEvent) =>
			{
				this.onProjectSearch(pEvent.target.value);
			});
		}

		// The main container render replaces the picker's mount element, so re-create the control into
		// the fresh node before anything can read a selection from it.
		this._renderRNIPicker();

		// Initial L5-section visibility for the freshly rendered DOM.
		this._refreshL5Sections();
	}
}

module.exports = ReportNamedInstanceProjectForms;

module.exports.default_configuration = (
{
	ViewIdentifier: 'ReportNamedInstanceProjectForms',

	DefaultRenderable: 'PF-Main-Container',
	DefaultDestinationAddress: '#FormEditor-Application-Container',

	AutoRender: false,

	CSS: /*css*/`
.pf-container
{
	display: flex;
	flex-direction: column;
	height: calc(100vh - 80px);
}
.pf-header
{
	padding: 16px 20px;
	background: var(--bulma-scheme-main-bis);
	border-bottom: 1px solid var(--bulma-border);
}
.pf-body
{
	flex: 1;
	display: flex;
	overflow: hidden;
}
.pf-sidebar
{
	width: 380px;
	min-width: 280px;
	border-right: 1px solid var(--bulma-border);
	display: flex;
	flex-direction: column;
	overflow: hidden;
}
.pf-sidebar-header
{
	padding: 12px 16px;
	border-bottom: 1px solid var(--bulma-border);
	background: var(--bulma-scheme-main-bis);
}
.pf-sidebar-list
{
	flex: 1;
	overflow-y: auto;
}
.pf-project-row
{
	cursor: pointer;
}
.pf-project-row:hover td
{
	background-color: var(--bulma-scheme-main-bis) !important;
}
/* --bulma-info-light is pinned to --bulma-info-90-l in BOTH schemes, so it stays near-white in
   dark mode while the row text goes near-white too (1.03:1 -- invisible). --bulma-info-soft and
   --bulma-info-bold are the self-inverting pair: 90%/20% lightness in light mode and 20%/90% in
   dark, so the highlight reads at ~7:1 either way. The color needs !important to beat Bulma's
   own .table tr.is-selected td { color: currentColor }, which is the more specific selector. */
.pf-project-row.is-selected td
{
	background-color: var(--bulma-info-soft) !important;
	color: var(--bulma-info-bold) !important;
}
.pf-detail
{
	flex: 1;
	overflow-y: auto;
	padding: 24px;
}
.pf-detail-content
{
	max-width: 800px;
}
.pf-empty-detail
{
	flex: 1;
	display: flex;
	align-items: center;
	justify-content: center;
	color: var(--bulma-text-soft);
}
.pf-add-section
{
	display: flex;
	align-items: flex-end;
	gap: 12px;
	margin-bottom: 1.5rem;
	flex-wrap: wrap;
}
.pf-add-section .field
{
	margin-bottom: 0;
	flex: 1;
	min-width: 250px;
}
/* The Link Form button sizes to its content; without this it is also a flex:1 field and takes half
   the row, leaving the picker — and so its dropdown — at half the width it could have. */
.pf-add-section .field.pf-add-action
{
	flex: 0 0 auto;
	min-width: 0;
}
.pf-drag-handle
{
	cursor: grab;
	user-select: none;
	text-align: center;
	width: 36px;
}
.pf-drag-handle:active
{
	cursor: grabbing;
}
tr.pf-dragging
{
	opacity: 0.3;
}
tr.pf-daily-row td
{
	background-color: var(--bulma-info-soft) !important;
	color: var(--bulma-info-bold) !important;
}
/* .has-text-grey is a fixed hsl(221,14%,48%) marked !important, so it ignores the row color above
   and drops to 1.8:1 on the dark soft background. Re-inherit the row color and carry the
   de-emphasis with opacity instead, which composites against whichever background the theme
   actually paints (~4.6:1 light, ~5.3:1 dark). */
.pf-project-row.is-selected td .has-text-grey,
tr.pf-daily-row td .has-text-grey
{
	color: inherit !important;
	opacity: 0.8;
}
/* A 3px bar with no text: it needs to read against the page in both schemes, which the soft fill
   does not manage (1.15:1 light, 2.18:1 dark). --bulma-info is 34% lightness in both, clearing
   the 3:1 non-text bar either way. */
tr.pf-drag-placeholder td
{
	background: var(--bulma-info) !important;
	height: 3px;
	padding: 0 !important;
	border: none !important;
}
.pf-bulkadd-list
{
	max-height: 400px;
	overflow-y: auto;
	padding: 8px 12px;
	border: 1px solid var(--bulma-border);
	border-radius: 4px;
	background: var(--bulma-scheme-main-bis);
}
/* LIMS Concierge switch. Uses the shared .button.is-toggle component (headlight-styles), which
   rotates the icon 180deg when inactive. Its colors are set explicitly here rather than via the
   is-success variant: that variant paints the off state with --bulma-success-weak, which is not
   defined in these apps, so the icon resolved to the inherited button color (white on the panel). */
/* .button.is-toggle is carried in the selectors on purpose: the shared component's own
   .button.is-toggle.is-active rule is more specific than a bare .pf-concierge-toggle.is-active
   and would otherwise repaint the on state back to --bulma-text. */
/* Flatten the button chrome so the switch reads as part of the card. The shared component clears
   background and border but not box-shadow, and Bulma's .button.is-active — which it also uses for
   "pressed" — zeroes the outer shadow. Since is-active here means "on", the stray shadow showed in
   the OFF state only. Killing it wins over the base/hover/focus shadows by specificity, so the
   keyboard focus ring is re-added as an outline below. */
.button.is-toggle.pf-concierge-toggle
{
	box-shadow: none;
	background: transparent;
}
.button.is-toggle.pf-concierge-toggle:focus-visible
{
	outline: 2px solid var(--bulma-link);
	outline-offset: 2px;
}
.button.is-toggle.pf-concierge-toggle i
{
	color: var(--bulma-text-weak);
}
.button.is-toggle.pf-concierge-toggle:not(.is-active):hover i
{
	color: var(--bulma-text-soft);
}
.button.is-toggle.pf-concierge-toggle.is-active i
{
	color: var(--bulma-success);
}
.button.is-toggle.pf-concierge-toggle[disabled]
{
	opacity: 0.5;
}
/* The picker paints its own control; the wrapper only needs to fill the field. */
.pf-rni-picker
{
	width: 100%;
}
/* Give the form NAME priority over the hash pill in the dropdown and in the selected value.
   PSP ships .pps-tag as flex:0 0 auto (never shrinks) while the label shrinks first, so a long name
   ellipsised while its hash stayed full width — backwards, since the name is what you search by.
   Anchored on the PPS_List_ / PPS_Value_ ids rather than a .pf-rni-picker descendant selector: the
   dropdown portals out to <body>, and those ids travel with it while a descendant selector would not.
   Scoped to this picker so other apps' pickers keep PSP's default balance. */
#PPS_List_PF-RNI-Picker .pps-option-label,
#PPS_Value_PF-RNI-Picker .pps-value
{
	flex: 1 1 auto;
}
/* Shrink factor 3 (vs the label's 1) so the hash yields first instead of the two splitting the
   overflow proportionally, plus a hard ceiling so a long hash can never take most of the row. At the
   normal content width both still render in full. */
#PPS_List_PF-RNI-Picker .pps-tag,
#PPS_Value_PF-RNI-Picker .pps-tag
{
	flex: 0 3 auto;
	min-width: 0;
	max-width: 45%;
	overflow: hidden;
	text-overflow: ellipsis;
	white-space: nowrap;
}
`,

	Templates:
	[
		{
			Hash: 'PF-Main-Container-Template',
			Template: /*html*/`
<div class="pf-container">
	<div id="PF-Notification-Area"></div>
	<div class="pf-header">
		<h2 class="title is-5 mb-0">
			<span class="icon"><i class="fas fa-diagram-project"></i></span>
			Project Forms
		</h2>
		<p class="subtitle is-6 has-text-grey mb-0">
			Select a project to manage which form definitions are linked to it.
		</p>
	</div>
	<div class="pf-body">
		<div class="pf-sidebar">
			<div class="pf-sidebar-header">
				<div class="control has-icons-left">
					<input id="PF-Project-Search" class="input is-small" type="text" placeholder="Search projects..." />
					<span class="icon is-left"><i class="fas fa-search"></i></span>
				</div>
			</div>
			<div class="pf-sidebar-list">
				<table class="table is-fullwidth is-hoverable is-narrow">
					<thead>
						<tr>
							<th style="width:90px">Project #</th>
							<th>Project</th>
							<th style="width:70px">Status</th>
						</tr>
					</thead>
					<tbody id="PF-Project-List-Body">
						<tr>
							<td colspan="3" class="has-text-centered">
								<span class="icon"><i class="fas fa-spinner fa-spin"></i></span>
								Loading...
							</td>
						</tr>
					</tbody>
				</table>
			</div>
		</div>

		<div id="PF-Empty-Detail" class="pf-empty-detail">
			<div class="has-text-centered">
				<span class="icon is-large"><i class="fas fa-arrow-left fa-2x"></i></span>
				<p class="mt-3">Select a project from the list to manage its form definitions.</p>
			</div>
		</div>

		<div id="PF-Detail-Panel" class="pf-detail is-hidden">
			<div class="pf-detail-content">
				<div id="PF-Selected-Project-Header" class="mb-5"></div>

				<div id="PF-LIMS-Concierge-Section" class="box mb-4 is-hidden">
					<div class="level mb-1">
						<div class="level-left">
							<div class="level-item">
								<div>
									<p class="title is-6 mb-1" id="PF-LIMS-Concierge-Title">
										<span class="icon"><i class="fas fa-flask"></i></span>
										LIMS Concierge
									</p>
									<p class="subtitle is-7 has-text-grey mb-0" id="PF-LIMS-Concierge-Status">
										Loading…
									</p>
								</div>
							</div>
						</div>
						<div class="level-right">
							<div class="level-item">
								<button type="button" id="PF-LIMS-Concierge-Toggle"
									class="button is-toggle pf-concierge-toggle"
									role="switch" aria-checked="false" aria-labelledby="PF-LIMS-Concierge-Title"
									onclick="_Pict.views.ReportNamedInstanceProjectForms.onLIMSConciergeToggle()">
									<span class="icon is-large"><i class="fas fa-fw fa-toggle-large-on"></i></span>
								</button>
							</div>
						</div>
					</div>
					<p id="PF-LIMS-Concierge-Warning" class="help is-warning is-hidden mt-2">
						<span class="icon"><i class="fas fa-triangle-exclamation"></i></span>
						Concierge is enabled but no LIMS forms are joined to this project.
						The materials API will log a fatal on each sample sync until LIMS forms are added.
					</p>
				</div>

				<div class="pf-add-section">
					<div class="field">
						<label class="label" id="PF-RNI-Picker-Label">Link a Form Definition</label>
						<div class="control">
							<div id="PF-RNI-Picker" class="pf-rni-picker"></div>
						</div>
					</div>
					<div class="field pf-add-action">
						<div class="control">
							<button id="PF-Add-Button" class="button is-info"
								onclick="_Pict.views.ReportNamedInstanceProjectForms.addFormToProject()">
								<span class="icon"><i class="fas fa-plus"></i></span>
								<span>Link Form</span>
							</button>
						</div>
					</div>
				</div>

				<div class="level mb-4">
					<div class="level-left">
						<div class="level-item">
							<h5 class="title is-6 mb-0">
								<span class="icon"><i class="fas fa-file-lines"></i></span>
								Linked Form Definitions
							</h5>
						</div>
					</div>
					<div class="level-right">
						<div class="level-item">
							<button id="PF-BulkAdd-Button" class="button is-small is-link is-outlined is-hidden"
								onclick="_Pict.views.ReportNamedInstanceProjectForms.openBulkAddModal()">
								<span class="icon"><i class="fas fa-layer-group"></i></span>
								<span>Bulk Add Forms</span>
							</button>
						</div>
						<div class="level-item">
							<button id="PF-Defaults-Button" class="button is-small is-success is-outlined"
								onclick="_Pict.views.ReportNamedInstanceProjectForms.pullInDefaultForms()">
								<span class="icon"><i class="fas fa-download"></i></span>
								<span>Customize Defaults</span>
							</button>
						</div>
						<div class="level-item">
							<button id="PF-RemoveAll-Button" class="button is-small is-danger is-outlined"
								onclick="_Pict.views.ReportNamedInstanceProjectForms.removeAllJoins()">
								<span class="icon"><i class="fas fa-trash-can"></i></span>
								<span>Remove All</span>
							</button>
						</div>
					</div>
				</div>
				<div id="PF-Daily-Status"></div>
				<div class="table-container">
					<table class="table is-fullwidth is-hoverable is-striped">
						<thead>
							<tr>
								<th style="width:36px"></th>
								<th style="width:50px">RNI ID</th>
								<th>Form Definition</th>
								<th style="width:130px">Type</th>
								<th style="width:70px">Ordinal</th>
								<th class="has-text-right" style="width:240px">Actions</th>
							</tr>
						</thead>
						<tbody id="PF-Joins-Body">
						</tbody>
					</table>
				</div>
			</div>
		</div>
	</div>

	<div id="PF-BulkAdd-Modal" class="modal">
		<div class="modal-background" onclick="_Pict.views.ReportNamedInstanceProjectForms.closeBulkAddModal()"></div>
		<div class="modal-card">
			<header class="modal-card-head">
				<p class="modal-card-title">
					<span class="icon"><i class="fas fa-layer-group"></i></span>
					Bulk Add Forms
				</p>
				<button class="delete" aria-label="close"
					onclick="_Pict.views.ReportNamedInstanceProjectForms.closeBulkAddModal()"></button>
			</header>
			<section class="modal-card-body">
				<div class="field">
					<label class="label">Form group</label>
					<div class="control">
						<div class="select is-fullwidth">
							<select id="PF-BulkAdd-Group-Select"
								onchange="_Pict.views.ReportNamedInstanceProjectForms.onBulkAddGroupChange()">
							</select>
						</div>
					</div>
					<p id="PF-BulkAdd-Group-Description" class="help has-text-grey"></p>
				</div>
				<div class="field">
					<div class="level mb-2">
						<div class="level-left">
							<div class="level-item">
								<p class="subtitle is-6 mb-0">Forms in this group not yet joined to the project</p>
							</div>
						</div>
						<div class="level-right">
							<div class="level-item">
								<button class="button is-small"
									onclick="_Pict.views.ReportNamedInstanceProjectForms.toggleAllBulkAddRows(true)">
									Select all
								</button>
								<button class="button is-small ml-2"
									onclick="_Pict.views.ReportNamedInstanceProjectForms.toggleAllBulkAddRows(false)">
									Clear
								</button>
							</div>
						</div>
					</div>
					<div id="PF-BulkAdd-List" class="pf-bulkadd-list">
						<p class="has-text-grey-light">Select a form group to see candidates.</p>
					</div>
				</div>
			</section>
			<footer class="modal-card-foot">
				<button id="PF-BulkAdd-Confirm-Button" class="button is-primary"
					onclick="_Pict.views.ReportNamedInstanceProjectForms.confirmBulkAdd()">
					Add selected
				</button>
				<button class="button"
					onclick="_Pict.views.ReportNamedInstanceProjectForms.closeBulkAddModal()">
					Cancel
				</button>
			</footer>
		</div>
	</div>
</div>`
		}
	],

	Renderables:
	[
		{
			RenderableHash: 'PF-Main-Container',
			TemplateHash: 'PF-Main-Container-Template',
			RenderMethod: 'replace'
		}
	]
});
